import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import { supabase, withAuthRetry } from './supabase';
import { logger } from './logger';
import { runMissingTranslations } from './referentialService';

// ─── Types ───────────────────────────────────────────────────────────

export interface ParsedSkillGroup {
  name: string;
  code: string;
  skills: ParsedSkill[];
}

export interface ParsedPosition {
  name: string;
  code: string;
  pole: string;
  groups: ParsedSkillGroup[];
}

export interface ParsedSkill {
  name: string;
  code: string;
}

export interface ImportPreview {
  subsectorName: string;
  positions: ParsedPosition[];
  totalGroups: number;
  totalSkills: number;
  /** Postes du sommaire sans fiche compétences — conservés, pas jetés. */
  positionsWithoutSkills: number;
}

export type ImportTable = 'positions' | 'skill_groups' | 'skills';

/** Phase 1 : ce que l'on sait du fichier avant même de l'ouvrir. */
export interface SourceValidation {
  ok: boolean;
  fileName: string;
  sizeLabel: string;
  kind: 'docx' | 'xlsx' | null;
  errors: string[];
  warnings: string[];
}

/**
 * Anomalie relevée en phase 3. `blocking` écarte la ligne de l'import,
 * `warning` la laisse passer mais demande une décision éclairée.
 */
export interface ImportIssue {
  level: 'blocking' | 'warning';
  target: string;
  detail: string;
}

interface PlannedItem {
  code: string;
  name: string;
  operation: 'create' | 'update';
  sortOrder: number;
}

export interface PlannedPosition extends PlannedItem {
  pole: string | null;
}

export interface PlannedGroup extends PlannedItem {
  positionCode: string;
}

export interface PlannedSkill extends PlannedItem {
  positionCode: string;
  groupCode: string;
}

/** Ligne existante que l'import va rendre obsolète. */
export interface RetireTarget {
  id: string;
  code: string;
  name: string;
}

export interface AnalyzeParams {
  preview: ImportPreview;
  sectorId: string;
  sectorCode: string;
  subsectorId: string | null;
  reactivateObsolete: boolean;
}

/** Phase 4 : le tampon. Tout est résolu ici, l'exécution ne fait plus de choix. */
export interface ImportPlan {
  sectorId: string;
  sectorCode: string;
  subsectorId: string | null;
  reactivateObsolete: boolean;
  /** Décidé à l'étape 4 : le fichier décrit-il le secteur en entier ? */
  obsoleteAbsentPositions: boolean;
  positions: PlannedPosition[];
  groups: PlannedGroup[];
  skills: PlannedSkill[];
  /** Contenu d'un poste repris par le fichier : toujours retiré. */
  replaced: { groups: RetireTarget[]; skills: RetireTarget[] };
  /** Postes du secteur absents du fichier : retirés seulement sur décision. */
  absent: { positions: RetireTarget[]; groups: RetireTarget[]; skills: RetireTarget[] };
  impact: {
    replacedSkillsInUse: number;
    absentPositionsOffers: number;
    absentPositionsExperiences: number;
  };
  issues: ImportIssue[];
  counts: {
    positionsNew: number;
    positionsUpdated: number;
    groupsNew: number;
    groupsUpdated: number;
    skillsNew: number;
    skillsUpdated: number;
    discarded: number;
  };
}

export interface ImportProgress {
  step: ImportTable | 'obsolescence' | 'traductions';
  processed: number;
  total: number;
  label: string;
}

export interface ImportFailure {
  table: ImportTable;
  code: string;
  name: string;
  cause: string;
}

/** Phase 6 : le rapport. */
export interface ImportReport {
  created: Record<ImportTable, number>;
  updated: Record<ImportTable, number>;
  /** Lignes passées en obsolète parce que le fichier ne les contient plus. */
  retired: Record<ImportTable, number>;
  discarded: number;
  /** Lignes jamais tentées parce que le traitement s'est arrêté avant elles. */
  notAttempted: number;
  failures: ImportFailure[];
  abortedReason: string | null;
  translationsQueued: number;
  durationMs: number;
}

/** Au-delà de ce nombre d'échecs d'affilée, on arrête : le problème est systémique. */
const MAX_CONSECUTIVE_FAILURES = 15;
/** Taille des lots d'écriture. Un lot = un aller-retour au lieu d'un par ligne. */
const WRITE_CHUNK = 250;
/** Taille des lots de lecture (contrainte de longueur d'URL sur le filtre `in`). */
const READ_CHUNK = 200;
/** PostgREST plafonne les réponses : sans pagination, un gros secteur serait tronqué. */
const READ_PAGE = 1000;
/**
 * Le code d'une compétence dérive de celui de son poste (`{poste}_{slug50}`) et
 * doit tenir dans 150 caractères : on borne donc le code de poste à 90.
 */
const POSITION_CODE_BUDGET = 90;

const MAX_CODE_LENGTH: Record<ImportTable, number> = {
  positions: 100,
  skill_groups: 150,
  skills: 150,
};

const TABLE_LABEL: Record<ImportTable, string> = {
  positions: 'Poste',
  skill_groups: 'Groupe',
  skills: 'Compétence',
};

// ─── Helpers ─────────────────────────────────────────────────────────

function slugify(text: string, maxLen = 90): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, maxLen);
}

/**
 * Forme de comparaison d'un intitulé : c'est elle qui décide si le fichier
 * parle d'un élément déjà en base. Insensible à la casse, aux accents, à la
 * ponctuation et aux espaces multiples, pour que « Aide(otte) ménager(ère) »
 * et « aide otte menager ere » soient reconnus comme le même poste.
 */
function normalizeName(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Ajoute un suffixe numéroté sans dépasser le budget de caractères. */
function withSuffix(base: string, index: number, budget: number): string {
  const suffix = `_${index}`;
  return `${base.slice(0, Math.max(1, budget - suffix.length))}${suffix}`;
}

function allocateCode(base: string, taken: Set<string>, budget: number): string {
  let candidate = base;
  let index = 2;
  while (taken.has(candidate)) candidate = withSuffix(base, index++, budget);
  taken.add(candidate);
  return candidate;
}

/**
 * Lecture paginée. Un secteur peut porter plusieurs milliers de compétences,
 * au-delà de ce que PostgREST renvoie en une fois.
 */
async function fetchPaged<T>(
  run: (from: number, to: number) => PromiseLike<{ data: unknown; error: { message: string } | null }>,
): Promise<T[]> {
  const rows: T[] = [];
  for (let from = 0; ; from += READ_PAGE) {
    const { data, error } = await run(from, from + READ_PAGE - 1);
    if (error) throw new Error(error.message);
    const page = (data as T[]) ?? [];
    rows.push(...page);
    if (page.length < READ_PAGE) return rows;
  }
}

// ─── Phase 1 : validation du format ──────────────────────────────────

const MAX_FILE_BYTES = 25 * 1024 * 1024;

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

export function validateSource(file: File): SourceValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  const lower = file.name.toLowerCase();

  let kind: SourceValidation['kind'] = null;
  if (lower.endsWith('.docx')) kind = 'docx';
  else if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) kind = 'xlsx';

  if (!kind) {
    errors.push(
      lower.endsWith('.doc')
        ? 'Le format .doc (Word 97) n’est pas lisible. Enregistrez le document en .docx.'
        : 'Format non reconnu. Seuls .docx, .xlsx et .xls sont acceptés.',
    );
  }
  if (file.size === 0) errors.push('Le fichier est vide.');
  if (file.size > MAX_FILE_BYTES) {
    errors.push(`Fichier trop volumineux (${humanSize(file.size)}, maximum ${humanSize(MAX_FILE_BYTES)}).`);
  }
  if (kind === 'xlsx') {
    warnings.push('Seul le premier onglet du classeur sera lu.');
  }

  return {
    ok: errors.length === 0,
    fileName: file.name,
    sizeLabel: humanSize(file.size),
    kind,
    errors,
    warnings,
  };
}

// ─── Parsing ─────────────────────────────────────────────────────────

/** « Aide à domicile (H/F) », « Maçon (F/H) », « Vendeur (H/F/X) ». */
const POSITION_MARKER = /\(\s*[hf]\s*\/\s*[fh](?:\s*\/\s*x)?\s*\)/i;

/** Pôles du référentiel HCR d'origine, conservés pour ne pas régresser. */
const LEGACY_POLE_KEYWORDS =
  /^\d*\s*(ACCUEIL|H[ÉE]BERGEMENT|RESTAURATION|TECHNIQUE|S[ÉE]CURIT|ANIMATION|COMMERCE)/i;

const POLE_PREFIX = /^\d*[\s.)-]*(p[ôo]les?|secteurs?|domaines?|familles?)\b/i;

function isMostlyUpperCase(line: string): boolean {
  const letters = line.replace(/[^\p{L}]/gu, '');
  if (letters.length < 3) return false;
  const upper = letters.replace(/[^\p{Lu}]/gu, '');
  return upper.length / letters.length >= 0.7;
}

function isPositionLine(line: string): boolean {
  if (line.length >= 120 || line.length < 3) return false;
  if (POSITION_MARKER.test(line)) return true;
  if (POLE_PREFIX.test(line) || LEGACY_POLE_KEYWORDS.test(line)) return false;
  if (isMostlyUpperCase(line)) return false;
  return /^[\p{L}][\p{L}\d\s'’\-\/]{2,70}$/u.test(line) && !/[.:]$/.test(line);
}

interface GroupLine {
  name: string;
  skillsText: string;
}

/**
 * Une ligne peut porter plusieurs groupes :
 *   « PILOTAGE OPÉRATIONNEL : Superviser. Coordonner. GESTION ADMIN : Valider. »
 * Le premier intitulé est en tête de ligne, casse libre. Les suivants sont
 * repérés après un point et doivent être en capitales : sans cette contrainte,
 * une phrase de compétence contenant un deux-points couperait le groupe en deux.
 * Le deux-points doit être suivi d'une espace, sinon « 14:30 » passerait pour
 * un nom de groupe.
 */
function splitColonGroups(line: string): GroupLine[] | null {
  const lead = line.match(/^(?:\d+[.)]\s*)?([^:.]{3,80}?)\s*:\s+(.{10,})$/);
  if (!lead) return null;

  const groups: GroupLine[] = [{ name: lead[1].trim(), skillsText: '' }];
  const rest = lead[2];
  const inline = /\.\s+([^.:]{3,60}?)\s*:\s+/g;
  let cursor = 0;
  let match: RegExpExecArray | null;

  while ((match = inline.exec(rest)) !== null) {
    if (!isMostlyUpperCase(match[1])) continue;
    groups[groups.length - 1].skillsText = rest.slice(cursor, match.index + 1).trim();
    groups.push({ name: match[1].trim(), skillsText: '' });
    cursor = inline.lastIndex;
  }
  groups[groups.length - 1].skillsText = rest.slice(cursor).trim();

  return groups;
}

/**
 * Trois écritures acceptées pour une ligne de groupe :
 *   « Accompagnement quotidien : Aider à la toilette. Préparer les repas. »
 *   « 1. Communication<TAB>Accueillir le client. Filtrer les appels. »
 *   « 1. Communication Client. Accueillir le client. »  (ancien format HCR)
 */
function matchGroupLines(line: string): GroupLine[] | null {
  const wide = line.match(/^(\d+)[.)]\s*(.+?)(?:\s{2,}|\t)(.+)$/);
  if (wide) return [{ name: wide[2].trim(), skillsText: wide[3].trim() }];

  const colon = splitColonGroups(line);
  if (colon) return colon;

  const numPrefix = line.match(/^(\d+)[.)]\s*/);
  if (numPrefix && /^\d+[.)]\s*[A-ZÀ-Ú][^.]+?\s+[A-ZÀ-Ú]/.test(line)) {
    const afterNum = line.slice(numPrefix[0].length);
    const parts = afterNum.split(/\.\s*/);
    return parts.length >= 2
      ? [{ name: parts[0].trim(), skillsText: parts.slice(1).join('. ') }]
      : [{ name: afterNum.slice(0, 50), skillsText: afterNum.slice(50) }];
  }

  return null;
}

/** « 12. Chef de rang » : un métier numéroté, pas un groupe de compétences. */
function numberedJobName(line: string): string | null {
  if (matchGroupLines(line)) return null;
  const match = line.match(/^\d+[.)]\s+(.+)$/);
  if (!match) return null;
  const name = match[1].replace(POSITION_MARKER, '').replace(/\s{2,}/g, ' ').trim();
  if (name.length < 3 || name.length > 90) return null;
  if (/[.:]$/.test(name) || name.includes(':') || name.includes('\t')) return null;
  if (POLE_PREFIX.test(name) || LEGACY_POLE_KEYWORDS.test(name)) return null;
  return name;
}

function isPoleHeader(line: string): boolean {
  if (line.length > 80 || isPositionLine(line) || matchGroupLines(line)) return false;
  return POLE_PREFIX.test(line) || LEGACY_POLE_KEYWORDS.test(line) || isMostlyUpperCase(line);
}

/**
 * Deux compétences identiques sous un même poste produiraient le même code.
 * Les groupes restés vides sont des faux positifs (intertitres, niveaux
 * d'expérience) : on les écarte avant de numéroter les codes.
 */
function finalizePosition(position: ParsedPosition) {
  const seen = new Set<string>();
  for (const group of position.groups) {
    group.skills = group.skills.filter((skill) => {
      if (seen.has(skill.code)) return false;
      seen.add(skill.code);
      return true;
    });
  }
  position.groups = position.groups.filter((group) => group.skills.length > 0);
  position.groups.forEach((group, index) => {
    group.code = `${position.code}_g${index + 1}`;
  });
}

function parseRawText(text: string): ImportPreview {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const positions: ParsedPosition[] = [];

  let subsectorName = '';
  let currentPole = '';
  let currentPosition: ParsedPosition | null = null;
  let currentGroup: ParsedSkillGroup | null = null;

  for (const line of lines) {
    if (!subsectorName) {
      const explicit = line.match(/^sous[- ]secteurs?\s*:\s*(.+)$/i);
      if (explicit) {
        subsectorName = explicit[1].trim();
        continue;
      }
      if (/^h[ôo]tellerie/i.test(line) && !line.includes('Secteur')) {
        subsectorName = line;
        continue;
      }
    }

    const numberedJob = numberedJobName(line);
    if (numberedJob) {
      currentPosition = {
        name: numberedJob,
        code: slugify(numberedJob),
        pole: currentPole,
        groups: [],
      };
      positions.push(currentPosition);
      currentGroup = null;
      continue;
    }

    if (isPoleHeader(line)) {
      currentPole = line.replace(/^\d+[\s.)-]*/, '').trim();
      continue;
    }

    if (isPositionLine(line)) {
      const name = line.replace(POSITION_MARKER, '').replace(/\s{2,}/g, ' ').trim();
      if (name.length < 3) continue;

      currentPosition = {
        name,
        code: slugify(name),
        pole: currentPole,
        groups: [],
      };
      positions.push(currentPosition);
      currentGroup = null;
      continue;
    }

    if (!currentPosition) continue;

    const groups = matchGroupLines(line);
    if (groups) {
      for (const group of groups) {
        if (group.name.length < 3) continue;
        currentGroup = { name: group.name, code: '', skills: [] };
        currentPosition.groups.push(currentGroup);
        if (group.skillsText) {
          parseSkillsIntoGroup(group.skillsText, currentPosition.code, currentGroup);
        }
      }
      continue;
    }

    if (currentGroup && line.length > 5 && !/^\d+[.)]/.test(line)) {
      parseSkillsIntoGroup(line, currentPosition.code, currentGroup);
    }
  }

  positions.forEach(finalizePosition);

  const positionsWithoutSkills = positions.filter((p) => p.groups.length === 0).length;
  const totalGroups = positions.reduce((s, p) => s + p.groups.length, 0);
  const totalSkills = positions.reduce((s, p) => s + p.groups.reduce((gs, g) => gs + g.skills.length, 0), 0);

  return { subsectorName, positions, totalGroups, totalSkills, positionsWithoutSkills };
}

function parseSkillsIntoGroup(text: string, positionCode: string, group: ParsedSkillGroup) {
  const phrases = text.split(/\.\s*/).filter(s => s.trim().length > 4);
  for (const phrase of phrases) {
    const cleanPhrase = phrase.trim().replace(/^\s*[-•]\s*/, '');
    if (cleanPhrase.length < 5) continue;

    const skillCode = `${positionCode}_${slugify(cleanPhrase, 50)}`;
    group.skills.push({
      name: cleanPhrase.charAt(0).toUpperCase() + cleanPhrase.slice(1),
      code: skillCode,
    });
  }
}

// ─── File parsing entry points ───────────────────────────────────────

export async function parseDocxFile(file: File): Promise<ImportPreview> {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return parseRawText(result.value);
}

export async function parseExcelFile(file: File): Promise<ImportPreview> {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const text = XLSX.utils.sheet_to_txt(sheet);
  return parseRawText(text);
}

/** Phase 2 : lecture du fichier validé en phase 1. */
export async function parseSource(file: File, kind: 'docx' | 'xlsx'): Promise<ImportPreview> {
  return kind === 'docx' ? parseDocxFile(file) : parseExcelFile(file);
}

// ─── Phase 3 : cohérence avec l'existant ─────────────────────────────

interface PositionRow {
  id: string;
  code: string;
  name_fr: string;
  active: boolean;
  sector_id: string | null;
}

interface ChildRow {
  id: string;
  code: string;
  name_fr: string;
  active: boolean;
  position_id: string | null;
}

const toRetire = (row: { id: string; code: string; name_fr: string }): RetireTarget => ({
  id: row.id,
  code: row.code,
  name: row.name_fr,
});

function groupByPosition<T extends { position_id: string | null }>(rows: T[]): Map<string, T[]> {
  const index = new Map<string, T[]>();
  for (const row of rows) {
    if (!row.position_id) continue;
    const bucket = index.get(row.position_id);
    if (bucket) bucket.push(row);
    else index.set(row.position_id, [row]);
  }
  return index;
}

/** Enfants directs d'un lot de postes, par tranches pour tenir dans l'URL. */
async function fetchChildren(table: 'skill_groups' | 'skills', positionIds: string[]): Promise<ChildRow[]> {
  const rows: ChildRow[] = [];
  for (let i = 0; i < positionIds.length; i += READ_CHUNK) {
    const slice = positionIds.slice(i, i + READ_CHUNK);
    rows.push(
      ...(await fetchPaged<ChildRow>((from, to) =>
        supabase
          .from(table)
          .select('id, code, name_fr, active, position_id')
          .in('position_id', slice)
          .range(from, to),
      )),
    );
  }
  return rows;
}

/** Nombre de lignes dont `column` tombe dans `values`, sans les rapatrier. */
async function countReferences(table: string, column: string, values: string[]): Promise<number> {
  let total = 0;
  for (let i = 0; i < values.length; i += READ_CHUNK) {
    const { count, error } = await supabase
      .from(table)
      .select('*', { count: 'exact', head: true })
      .in(column, values.slice(i, i + READ_CHUNK));
    if (error) throw error;
    total += count ?? 0;
  }
  return total;
}

/**
 * Les codes d'enfants dérivent de celui du poste, donc une collision hors du
 * secteur est improbable — mais un code posé à la main pourrait coïncider.
 * On vérifie, on renumérote, et on renvoie la correspondance pour répercuter
 * le changement sur les références.
 */
async function resolveCollisions(
  table: ImportTable,
  items: PlannedItem[],
  taken: Set<string>,
  budget: number,
): Promise<Map<string, string>> {
  const fresh = items.filter((item) => item.operation === 'create').map((item) => item.code);
  if (fresh.length === 0) return new Map();

  const clashing = new Set<string>();
  for (let i = 0; i < fresh.length; i += READ_CHUNK) {
    const { data, error } = await supabase
      .from(table)
      .select('code')
      .in('code', fresh.slice(i, i + READ_CHUNK));
    if (error) throw error;
    for (const row of (data as { code: string }[]) ?? []) clashing.add(row.code);
  }
  if (clashing.size === 0) return new Map();

  const remap = new Map<string, string>();
  for (const item of items) {
    if (item.operation !== 'create' || !clashing.has(item.code)) continue;
    taken.add(item.code);
    const next = allocateCode(item.code, taken, budget);
    remap.set(item.code, next);
    item.code = next;
  }
  return remap;
}

/**
 * Confronte le contenu reconnu au référentiel déjà en base et produit le plan
 * d'exécution. Rien n'est écrit ici : l'admin voit l'impact avant de décider.
 *
 * Règle d'identité : un poste est reconnu par son intitulé à l'intérieur du
 * secteur cible. Deux postes de même intitulé dans deux secteurs sont deux
 * postes distincts, et le poste de l'autre secteur n'est jamais touché.
 */
export async function analyzeImport(params: AnalyzeParams): Promise<ImportPlan> {
  const { preview, sectorId, sectorCode, subsectorId, reactivateObsolete } = params;

  const issues: ImportIssue[] = [];
  let discarded = 0;

  const discard = (table: ImportTable, name: string, detail: string) => {
    discarded++;
    issues.push({ level: 'blocking', target: `${TABLE_LABEL[table]} « ${name} »`, detail });
  };
  const warn = (target: string, detail: string) => {
    issues.push({ level: 'warning', target, detail });
  };

  // ── État réel de la base ────────────────────────────────────────────
  // Tous les postes : le secteur cible pour l'appariement, les autres pour
  // garantir l'unicité globale des codes et signaler les homonymes.
  const allPositions = await fetchPaged<PositionRow>((from, to) =>
    withAuthRetry(() => supabase.from('positions').select('id, code, name_fr, active, sector_id').range(from, to)),
  );
  const sectorPositions = allPositions.filter((position) => position.sector_id === sectorId);
  const sectorPositionIds = sectorPositions.map((position) => position.id);

  const [existingGroups, existingSkills, sectorRows] = await Promise.all([
    fetchChildren('skill_groups', sectorPositionIds),
    fetchChildren('skills', sectorPositionIds),
    withAuthRetry(() => supabase.from('sectors').select('id, name_fr')),
  ]);

  const sectorNames = new Map(
    ((sectorRows.data as { id: string; name_fr: string }[]) ?? []).map((row) => [row.id, row.name_fr]),
  );
  const takenPositionCodes = new Set(allPositions.map((position) => position.code));
  const takenGroupCodes = new Set(existingGroups.map((group) => group.code));
  const takenSkillCodes = new Set(existingSkills.map((skill) => skill.code));

  const positionByName = new Map(sectorPositions.map((p) => [normalizeName(p.name_fr), p]));
  const positionByCode = new Map(sectorPositions.map((p) => [p.code, p]));
  const groupsByPosition = groupByPosition(existingGroups);
  const skillsByPosition = groupByPosition(existingSkills);

  const elsewhereByName = new Map<string, PositionRow[]>();
  for (const position of allPositions) {
    if (position.sector_id === sectorId) continue;
    const key = normalizeName(position.name_fr);
    const bucket = elsewhereByName.get(key);
    if (bucket) bucket.push(position);
    else elsewhereByName.set(key, [position]);
  }

  // ── Appariement ─────────────────────────────────────────────────────
  const positions: PlannedPosition[] = [];
  const groups: PlannedGroup[] = [];
  const skills: PlannedSkill[] = [];
  const replaced = { groups: [] as RetireTarget[], skills: [] as RetireTarget[] };
  const matchedPositionIds = new Set<string>();
  const seenPositionNames = new Set<string>();

  for (const [posIdx, pos] of preview.positions.entries()) {
    const nameKey = normalizeName(pos.name);
    if (seenPositionNames.has(nameKey)) {
      discard('positions', pos.name, 'Ce poste apparaît déjà plus haut dans le fichier.');
      continue;
    }
    seenPositionNames.add(nameKey);

    const expectedCode =
      slugify(`${sectorCode}_${pos.name}`, POSITION_CODE_BUDGET) || slugify(`${sectorCode}_p${posIdx + 1}`, POSITION_CODE_BUDGET);
    const existing = positionByName.get(nameKey) ?? positionByCode.get(expectedCode);

    // Un code déjà en place n'est jamais renommé : les offres et les documents
    // candidat en gardent une copie textuelle sans clé étrangère.
    const positionCode = existing
      ? existing.code
      : allocateCode(expectedCode, takenPositionCodes, POSITION_CODE_BUDGET);

    if (existing) {
      matchedPositionIds.add(existing.id);
      if (!existing.active) {
        warn(
          `Poste « ${pos.name} »`,
          reactivateObsolete
            ? 'Poste obsolète de ce secteur : il sera réactivé.'
            : 'Poste obsolète de ce secteur : il restera obsolète, seul son contenu sera mis à jour.',
        );
      }
    } else {
      const elsewhere = elsewhereByName.get(nameKey) ?? [];
      if (elsewhere.length > 0) {
        const where = elsewhere
          .map((p) => sectorNames.get(p.sector_id ?? '') ?? 'secteur inconnu')
          .join(', ');
        warn(
          `Poste « ${pos.name} »`,
          `Un poste du même intitulé existe dans ${where} : il n'est pas modifié. Un poste distinct est créé dans le secteur cible.`,
        );
      }
    }

    positions.push({
      code: positionCode,
      name: pos.name,
      operation: existing ? 'update' : 'create',
      sortOrder: posIdx,
      pole: pos.pole || null,
    });

    const childGroups = existing ? groupsByPosition.get(existing.id) ?? [] : [];
    const childSkills = existing ? skillsByPosition.get(existing.id) ?? [] : [];
    const groupByName = new Map(childGroups.map((group) => [normalizeName(group.name_fr), group]));
    const skillByName = new Map(childSkills.map((skill) => [normalizeName(skill.name_fr), skill]));
    const keptGroupIds = new Set<string>();
    const keptSkillIds = new Set<string>();
    const seenGroupNames = new Set<string>();
    const seenSkillNames = new Set<string>();

    for (const [grpIdx, grp] of pos.groups.entries()) {
      const groupKey = normalizeName(grp.name);
      if (seenGroupNames.has(groupKey)) {
        discard('skill_groups', grp.name, `Groupe déjà présent sous le poste « ${pos.name} » dans le fichier.`);
        continue;
      }
      seenGroupNames.add(groupKey);

      const existingGroup = groupByName.get(groupKey);
      if (existingGroup) keptGroupIds.add(existingGroup.id);
      const groupCode = existingGroup
        ? existingGroup.code
        : allocateCode(`${positionCode}_g${grpIdx + 1}`, takenGroupCodes, MAX_CODE_LENGTH.skill_groups);

      groups.push({
        code: groupCode,
        name: grp.name,
        operation: existingGroup ? 'update' : 'create',
        sortOrder: grpIdx,
        positionCode,
      });

      for (const [skIdx, sk] of grp.skills.entries()) {
        const skillKey = normalizeName(sk.name);
        if (seenSkillNames.has(skillKey)) {
          discard('skills', sk.name, `Compétence déjà présente sous le poste « ${pos.name} » dans le fichier.`);
          continue;
        }
        seenSkillNames.add(skillKey);

        const existingSkill = skillByName.get(skillKey);
        if (existingSkill) keptSkillIds.add(existingSkill.id);
        const slug = slugify(sk.name, 50) || `s${skIdx + 1}`;
        const skillCode = existingSkill
          ? existingSkill.code
          : allocateCode(`${positionCode}_${slug}`, takenSkillCodes, MAX_CODE_LENGTH.skills);

        skills.push({
          code: skillCode,
          name: sk.name,
          operation: existingSkill ? 'update' : 'create',
          sortOrder: skIdx,
          positionCode,
          groupCode,
        });
      }
    }

    // « Si un poste existe alors on remplace ses compétences » : ce que le
    // fichier ne reprend plus sort du référentiel.
    if (existing) {
      const droppedGroups = childGroups.filter((group) => group.active && !keptGroupIds.has(group.id));
      const droppedSkills = childSkills.filter((skill) => skill.active && !keptSkillIds.has(skill.id));
      replaced.groups.push(...droppedGroups.map(toRetire));
      replaced.skills.push(...droppedSkills.map(toRetire));
      if (droppedGroups.length > 0 || droppedSkills.length > 0) {
        warn(
          `Poste « ${pos.name} »`,
          `Poste déjà présent dans ce secteur : ${droppedSkills.length} compétence(s) et ${droppedGroups.length} groupe(s) absents du fichier deviendront obsolètes.`,
        );
      }
    }
  }

  // ── Ce que le fichier ne mentionne pas ──────────────────────────────
  const absentPositionRows = sectorPositions.filter(
    (position) => position.active && !matchedPositionIds.has(position.id),
  );
  const absentIds = new Set(absentPositionRows.map((position) => position.id));
  const absent = {
    positions: absentPositionRows.map(toRetire),
    groups: existingGroups
      .filter((group) => group.active && group.position_id && absentIds.has(group.position_id))
      .map(toRetire),
    skills: existingSkills
      .filter((skill) => skill.active && skill.position_id && absentIds.has(skill.position_id))
      .map(toRetire),
  };

  if (absent.positions.length > 0) {
    const sample = absent.positions.slice(0, 8).map((p) => p.name).join(', ');
    warn(
      'Secteur cible',
      `${absent.positions.length} poste(s) de ce secteur ne figurent pas dans le fichier (${sample}${absent.positions.length > 8 ? ', …' : ''}). ` +
        'Ils ne seront rendus obsolètes que si vous le demandez à l’étape 4.',
    );
  }

  // ── Codes neufs : dernière vérification d'unicité ───────────────────
  const groupRemap = await resolveCollisions('skill_groups', groups, takenGroupCodes, MAX_CODE_LENGTH.skill_groups);
  if (groupRemap.size > 0) {
    for (const skill of skills) skill.groupCode = groupRemap.get(skill.groupCode) ?? skill.groupCode;
  }
  await resolveCollisions('skills', skills, takenSkillCodes, MAX_CODE_LENGTH.skills);

  // ── Impact sur les données utilisateur ──────────────────────────────
  const [replacedSkillsInUse, absentPositionsOffers, absentPositionsExperiences] = await Promise.all([
    countReferences('candidate_experience_skills', 'skill_id', replaced.skills.map((s) => s.id)),
    countReferences('job_offers', 'position_code', absent.positions.map((p) => p.code)),
    countReferences('candidate_experiences', 'position_id', absent.positions.map((p) => p.id)),
  ]);

  if (replacedSkillsInUse > 0) {
    warn(
      'Compétences retirées',
      `${replacedSkillsInUse} expérience(s) candidat citent des compétences qui deviendront obsolètes. Elles restent affichées, mais ne seront plus proposées à la saisie.`,
    );
  }
  if (absentPositionsOffers > 0 || absentPositionsExperiences > 0) {
    warn(
      'Postes absents du fichier',
      `${absentPositionsOffers} offre(s) et ${absentPositionsExperiences} expérience(s) candidat référencent ces postes. ` +
        'À ne rendre obsolètes que si le fichier décrit bien le secteur en entier.',
    );
  }

  const count = (list: PlannedItem[], op: 'create' | 'update') =>
    list.filter((item) => item.operation === op).length;

  return {
    sectorId,
    sectorCode,
    subsectorId,
    reactivateObsolete,
    obsoleteAbsentPositions: false,
    positions,
    groups,
    skills,
    replaced,
    absent,
    impact: { replacedSkillsInUse, absentPositionsOffers, absentPositionsExperiences },
    issues,
    counts: {
      positionsNew: count(positions, 'create'),
      positionsUpdated: count(positions, 'update'),
      groupsNew: count(groups, 'create'),
      groupsUpdated: count(groups, 'update'),
      skillsNew: count(skills, 'create'),
      skillsUpdated: count(skills, 'update'),
      discarded,
    },
  };
}

// ─── Phase 5 : création graduelle ────────────────────────────────────

interface RunContext {
  report: ImportReport;
  consecutiveFailures: number;
  shouldStop: () => boolean;
  onProgress: (progress: ImportProgress) => void;
}

function abortIfSystemic(ctx: RunContext): boolean {
  if (ctx.report.abortedReason) return true;
  if (ctx.consecutiveFailures < MAX_CONSECUTIVE_FAILURES) return false;
  ctx.report.abortedReason =
    `Arrêt après ${MAX_CONSECUTIVE_FAILURES} échecs consécutifs : le problème n’est pas ponctuel. ` +
    `Dernière cause : ${ctx.report.failures[ctx.report.failures.length - 1]?.cause ?? 'inconnue'}`;
  return true;
}

/**
 * Écrit un lot en une requête, et ne retombe ligne à ligne que si le lot échoue.
 * On garde ainsi la vitesse du traitement groupé tout en isolant la ligne fautive
 * au lieu de perdre les 249 autres.
 */
async function writeChunk(
  table: ImportTable,
  rows: Record<string, unknown>[],
  labelByCode: Map<string, string>,
  operation: 'create' | 'update',
  ctx: RunContext,
  idByCode: Map<string, string>,
): Promise<void> {
  const { data, error } = await supabase
    .from(table)
    .upsert(rows, { onConflict: 'code' })
    .select('id, code');

  if (!error) {
    for (const row of (data as { id: string; code: string }[]) || []) idByCode.set(row.code, row.id);
    ctx.report[operation === 'create' ? 'created' : 'updated'][table] += rows.length;
    ctx.consecutiveFailures = 0;
    return;
  }

  for (const row of rows) {
    if (ctx.shouldStop() || abortIfSystemic(ctx)) return;
    const code = String(row.code);
    const { data: single, error: rowError } = await supabase
      .from(table)
      .upsert(row, { onConflict: 'code' })
      .select('id, code')
      .single();

    if (rowError) {
      ctx.report.failures.push({
        table,
        code,
        name: labelByCode.get(code) ?? code,
        cause: rowError.message,
      });
      ctx.consecutiveFailures++;
      continue;
    }
    if (single) idByCode.set(single.code, single.id);
    ctx.report[operation === 'create' ? 'created' : 'updated'][table] += 1;
    ctx.consecutiveFailures = 0;
  }
}

type RowDraft = { row: Record<string, unknown> } | { skipCause: string };

async function writeTable(
  table: ImportTable,
  items: PlannedItem[],
  buildRow: (item: PlannedItem, operation: 'create' | 'update') => RowDraft,
  ctx: RunContext,
  idByCode: Map<string, string>,
  offset: number,
  grandTotal: number,
): Promise<number> {
  const labelByCode = new Map(items.map((item) => [item.code, item.name]));
  let processed = 0;

  for (const operation of ['create', 'update'] as const) {
    if (ctx.shouldStop() || abortIfSystemic(ctx)) return processed;
    const rows: Record<string, unknown>[] = [];
    for (const item of items.filter((candidate) => candidate.operation === operation)) {
      const draft = buildRow(item, operation);
      if ('row' in draft) {
        rows.push(draft.row);
        continue;
      }
      ctx.report.discarded++;
      ctx.report.failures.push({ table, code: item.code, name: item.name, cause: draft.skipCause });
    }

    for (let i = 0; i < rows.length; i += WRITE_CHUNK) {
      if (ctx.shouldStop() || abortIfSystemic(ctx)) return processed;
      const chunk = rows.slice(i, i + WRITE_CHUNK);
      ctx.onProgress({
        step: table,
        processed: offset + processed,
        total: grandTotal,
        label: `${TABLE_LABEL[table]}s : ${processed + chunk.length}/${items.length}`,
      });
      await writeChunk(table, chunk, labelByCode, operation, ctx, idByCode);
      processed += chunk.length;
    }
  }

  return processed;
}

/**
 * Passage en obsolète par lots. Le trigger `stamp_referential_deprecation`
 * pose `deprecated_at` et `deprecated_by` tout seul : on ne touche qu'`active`.
 * Rien n'est supprimé, les offres et documents qui citent ces codes restent
 * lisibles.
 */
async function retireTable(
  table: ImportTable,
  targets: RetireTarget[],
  ctx: RunContext,
  offset: number,
  grandTotal: number,
): Promise<number> {
  let processed = 0;

  for (let i = 0; i < targets.length; i += WRITE_CHUNK) {
    if (ctx.shouldStop() || abortIfSystemic(ctx)) return processed;
    const chunk = targets.slice(i, i + WRITE_CHUNK);
    ctx.onProgress({
      step: 'obsolescence',
      processed: offset + processed,
      total: grandTotal,
      label: `Mise en obsolescence — ${TABLE_LABEL[table].toLowerCase()}s : ${processed + chunk.length}/${targets.length}`,
    });

    const { error } = await supabase
      .from(table)
      .update({ active: false })
      .in('id', chunk.map((target) => target.id));

    if (!error) {
      ctx.report.retired[table] += chunk.length;
      ctx.consecutiveFailures = 0;
      processed += chunk.length;
      continue;
    }

    for (const target of chunk) {
      if (ctx.shouldStop() || abortIfSystemic(ctx)) return processed;
      const { error: rowError } = await supabase.from(table).update({ active: false }).eq('id', target.id);
      if (rowError) {
        ctx.report.failures.push({ table, code: target.code, name: target.name, cause: rowError.message });
        ctx.consecutiveFailures++;
      } else {
        ctx.report.retired[table] += 1;
        ctx.consecutiveFailures = 0;
      }
    }
    processed += chunk.length;
  }

  return processed;
}

export interface ImportOutcome {
  report: ImportReport;
  /** Identifiants des lignes créées, pour la phase de traduction. */
  createdIds: Record<ImportTable, string[]>;
}

export async function executeImportPlan(
  plan: ImportPlan,
  onProgress: (progress: ImportProgress) => void,
  shouldStop: () => boolean,
): Promise<ImportOutcome> {
  const startedAt = Date.now();
  const report: ImportReport = {
    created: { positions: 0, skill_groups: 0, skills: 0 },
    updated: { positions: 0, skill_groups: 0, skills: 0 },
    retired: { positions: 0, skill_groups: 0, skills: 0 },
    discarded: 0,
    notAttempted: 0,
    failures: [],
    abortedReason: null,
    translationsQueued: 0,
    durationMs: 0,
  };
  const ctx: RunContext = { report, consecutiveFailures: 0, shouldStop, onProgress };

  const positionIds = new Map<string, string>();
  const groupIds = new Map<string, string>();
  const skillIds = new Map<string, string>();
  // Ce qu'un poste apparié ne contient plus sort toujours du référentiel. Les
  // postes absents du fichier ne sortent que si l'admin a confirmé à l'étape 4
  // que le fichier décrit bien le secteur en entier.
  const retiring: Record<ImportTable, RetireTarget[]> = {
    skills: [...plan.replaced.skills, ...(plan.obsoleteAbsentPositions ? plan.absent.skills : [])],
    skill_groups: [...plan.replaced.groups, ...(plan.obsoleteAbsentPositions ? plan.absent.groups : [])],
    positions: plan.obsoleteAbsentPositions ? plan.absent.positions : [],
  };
  const grandTotal =
    plan.positions.length +
    plan.groups.length +
    plan.skills.length +
    retiring.skills.length +
    retiring.skill_groups.length +
    retiring.positions.length;

  const finish = (): ImportOutcome => {
    // Tout ce qui n'a été ni écrit ni mis en échec n'a jamais été tenté :
    // le traitement s'est arrêté avant d'y arriver.
    const handled =
      report.created.positions + report.created.skill_groups + report.created.skills +
      report.updated.positions + report.updated.skill_groups + report.updated.skills +
      report.retired.positions + report.retired.skill_groups + report.retired.skills +
      report.failures.length;
    report.notAttempted = Math.max(0, grandTotal - handled);
    report.durationMs = Date.now() - startedAt;
    logger.functional('execute_import_plan', 'referentialImportService', {
      created: report.created,
      updated: report.updated,
      retired: report.retired,
      failures: report.failures.length,
      notAttempted: report.notAttempted,
      aborted: Boolean(report.abortedReason),
      durationMs: report.durationMs,
    });

    const newIds = (items: PlannedItem[], ids: Map<string, string>) =>
      items
        .filter((item) => item.operation === 'create')
        .map((item) => ids.get(item.code))
        .filter((id): id is string => Boolean(id));

    return {
      report,
      createdIds: {
        positions: newIds(plan.positions, positionIds),
        skill_groups: newIds(plan.groups, groupIds),
        skills: newIds(plan.skills, skillIds),
      },
    };
  };

  let done = await writeTable(
    'positions',
    plan.positions,
    (item, operation) => {
      const position = item as PlannedPosition;
      const row: Record<string, unknown> = {
        code: position.code,
        name_fr: position.name,
        sector_id: plan.sectorId,
        subsector_id: plan.subsectorId,
        category: position.pole,
      };
      // sort_order et active ne sont posés qu'à la création : une réimportation
      // ne doit pas réordonner ni ressusciter ce que l'admin a rangé ou rendu obsolète.
      if (operation === 'create') {
        row.sort_order = position.sortOrder;
        row.active = true;
      } else if (plan.reactivateObsolete) {
        row.active = true;
      }
      return { row };
    },
    ctx,
    positionIds,
    0,
    grandTotal,
  );

  // Inutile d'énumérer chaque enfant orphelin quand on s'est déjà arrêté :
  // `finish` les compte, le rapport reste lisible.
  const stopped = () => Boolean(report.abortedReason) || shouldStop();
  if (stopped()) return finish();

  done += await writeTable(
    'skill_groups',
    plan.groups,
    (item, operation) => {
      const group = item as PlannedGroup;
      const positionId = positionIds.get(group.positionCode);
      if (!positionId) return { skipCause: `Poste parent « ${group.positionCode} » non créé.` };
      const row: Record<string, unknown> = {
        code: group.code,
        name_fr: group.name,
        position_id: positionId,
      };
      if (operation === 'create') {
        row.sort_order = group.sortOrder;
        row.active = true;
      } else if (plan.reactivateObsolete) {
        row.active = true;
      }
      return { row };
    },
    ctx,
    groupIds,
    done,
    grandTotal,
  );

  if (stopped()) return finish();

  done += await writeTable(
    'skills',
    plan.skills,
    (item, operation) => {
      const skill = item as PlannedSkill;
      const positionId = positionIds.get(skill.positionCode);
      const groupId = groupIds.get(skill.groupCode);
      if (!positionId) return { skipCause: `Poste parent « ${skill.positionCode} » non créé.` };
      if (!groupId) return { skipCause: `Groupe parent « ${skill.groupCode} » non créé.` };
      const row: Record<string, unknown> = {
        code: skill.code,
        name_fr: skill.name,
        sector_id: plan.sectorId,
        position_id: positionId,
        skill_group_id: groupId,
      };
      if (operation === 'create') {
        row.sort_order = skill.sortOrder;
        row.active = true;
      } else if (plan.reactivateObsolete) {
        row.active = true;
      }
      return { row };
    },
    ctx,
    skillIds,
    done,
    grandTotal,
  );

  if (stopped()) return finish();

  // Des enfants d'abord : si l'on s'arrête en route, on ne laisse pas un poste
  // obsolète au-dessus de compétences encore actives.
  done += await retireTable('skills', retiring.skills, ctx, done, grandTotal);
  if (stopped()) return finish();
  done += await retireTable('skill_groups', retiring.skill_groups, ctx, done, grandTotal);
  if (stopped()) return finish();
  await retireTable('positions', retiring.positions, ctx, done, grandTotal);

  return finish();
}

/**
 * Traductions des nouveaux libellés vers toutes les langues cibles. Séparé de
 * l'import : chaque lot déclenche des appels GPT et prenait jusqu'ici le pas sur
 * l'écriture des données, donnant l'impression d'un import qui ne finit pas.
 * Mode « manquantes uniquement » : un libellé mis à jour mais déjà traduit
 * n'est pas réécrit ici (la retraduction sur changement de name_fr est gérée
 * par updateReferentialItem).
 */
export async function translateImported(
  ids: Record<ImportTable, string[]>,
  onProgress: (progress: ImportProgress) => void,
  shouldStop: () => boolean,
): Promise<number> {
  const total = Object.values(ids).reduce((sum, list) => sum + list.length, 0);
  let done = 0;

  for (const table of Object.keys(ids) as ImportTable[]) {
    if (ids[table].length === 0) continue;
    if (shouldStop()) return done;
    const base = done;
    try {
      const result = await runMissingTranslations({
        tables: [table],
        ids: ids[table],
        shouldStop,
        onProgress: (p) => {
          const processed = base + Math.min(ids[table].length, p.translated + p.skipped + p.failed);
          onProgress({ step: 'traductions', processed, total, label: `Traduction ${processed}/${total}` });
        },
        onError: (msg) => logger.error('batch_translate', 'referentialImportService', { table, error: msg }),
      });
      done += result.translated + result.skipped;
      if (result.stopped) return done;
    } catch (err) {
      logger.error('batch_translate', 'referentialImportService', { table, error: String(err) });
    }
  }
  return done;
}
